import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Operation } from 'fast-json-patch'

import { useUpdateProfile } from '../../../../hooks/profile/useUpdateProfile'

import Address from '../../../address/Address'

import { useApp } from '../../../../contexts/AppContext'
import { useProfileContext } from '../../../../contexts/ProfileContext'
import { BannerKind } from '../../../../types/contexts/AppContext'
import { UserProfile } from '../../../../types/user/userProfile'

type BillingProps = {
  profile: UserProfile | null
  requiredOnly?: boolean
  onSaveSuccess?: () => void
  onSkip?: () => void
  saveLabel?: string
  skipLabel?: string
}

const Billing = ({
  profile,
  requiredOnly = false,
  onSaveSuccess,
  onSkip,
  saveLabel,
  skipLabel,
}: BillingProps) => {
  const { t } = useTranslation('account')
  const { showBanner } = useApp()
  const { updateLocalProfile } = useProfileContext()

  const { updateProfile, loading, error, success, resetState } =
    useUpdateProfile()

  useEffect(() => {
    if (!error && !success) {
      return
    }

    if (error) {
      showBanner({
        kind: BannerKind.ERR,
        msg: error,
      })
    }

    if (success) {
      showBanner({
        kind: BannerKind.OK,
        msg: success,
      })
    }

    resetState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, success])

  const handleSave = async (patch: Operation[]) => {
    const updatedProfile = await updateProfile(
      patch.map(operation => ({
        ...operation,
        path: `/address${operation.path}`,
      })),
      t('account.hook.billing.saved')
    )

    if (updatedProfile) {
      updateLocalProfile({ address: updatedProfile.address })
      onSaveSuccess?.()
    }

    return updatedProfile
  }

  return (
    <Address
      address={profile?.address ?? null}
      title={t('account.billing.address.title')}
      subtitle={t('account.billing.address.subtitle')}
      requiredOnly={requiredOnly}
      loading={loading}
      onSave={handleSave}
      onSkip={onSkip}
      saveLabel={saveLabel}
      skipLabel={skipLabel}
    />
  )
}

export default Billing
